import { safelyJoinPaths } from '@trarc/pc-shared';
import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import { useAppContainerContext } from '../../../contexts/AppContainerContext';
import { useSessionContext } from '../../../contexts/SessionContext';
import { useUpdatePageGroups } from '../../../hooks';
import { useCreateNavigationEventMutation } from '../../../types/api.graphql';
import { SideBarPageGroups } from '../../../types/enums';
import { HELP_TAB_ENTITIES } from '../constants';
import { HelpTabNavItem } from '../types';
import {
  PageGroupProps,
  StyledButton,
  StyledControlledCollapse,
  StyledHelpIcon,
  StyledNavLink,
} from './GeneralPageGroup.styles';
import { TitleIconRow } from './TitleIconRow';

const HelpPageGroup = ({ isExpanded }: PageGroupProps) => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const previousLocation = useRef<string | null>(null);
  const { setPreviousPath } = useSessionContext();
  const [createNavigationEvent] = useCreateNavigationEventMutation();
  const { setShowContactModal } = useAppContainerContext();
  const { openPageGroups, updateOpenPageGroups } = useUpdatePageGroups();

  const isHelpCollapseOpen = isExpanded && openPageGroups.includes(SideBarPageGroups.Help);

  useEffect(() => {
    previousLocation.current = pathname;
  }, []);

  const logNavigationEvent = (navItem: HelpTabNavItem): void => {
    const toPath = t(HELP_TAB_ENTITIES.find((entity) => entity.value === navItem).path);

    setPreviousPath(toPath);

    void createNavigationEvent({
      variables: {
        input: {
          toPathname: toPath,
          fromPathname: previousLocation.current,
        },
      },
    });
    previousLocation.current = toPath;
  };

  return (
    <StyledControlledCollapse
      title={
        <TitleIconRow
          icon={<StyledHelpIcon />}
          title={t('help.title')}
          $isExpanded={isExpanded}
          open={isHelpCollapseOpen}
        />
      }
      onClick={() => updateOpenPageGroups(SideBarPageGroups.Help)}
      open={isHelpCollapseOpen}
      $isExpanded={isExpanded}
    >
      <StyledButton
        onClick={() => {
          setShowContactModal(true);
          logNavigationEvent(HelpTabNavItem.ContactUs);
        }}
      >
        {t('contactUs.label')}
      </StyledButton>
      <StyledNavLink
        to={safelyJoinPaths(process.env.REACT_APP_GRAPHQL_URI, 'api', 'help')}
        target="_blank"
        onClick={() => logNavigationEvent(HelpTabNavItem.Documentation)}
      >
        {t('help.documentation')}
      </StyledNavLink>
    </StyledControlledCollapse>
  );
};

export default HelpPageGroup;
